// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useEffect, useState, useCallback } from "react";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import type { ILocalAgentClient } from "../../services/localAgentClient";
import { LocalAgentError } from "../../services/localAgentClient";
import type { WizardStepProps } from "../EnrollmentWizard";
import PinDialog from "../PinDialog";

export interface StoreCertificateStepProps extends WizardStepProps {
  localAgentClient: ILocalAgentClient;
}

export default function StoreCertificateStep({
  state,
  setState,
  onComplete,
  onError,
  localAgentClient,
}: StoreCertificateStepProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [pinDialogVisible, setPinDialogVisible] = useState(false);
  const [pinErrorMessage, setPinErrorMessage] = useState<string | undefined>();

  const doStore = useCallback(async (pin?: string) => {
    if (!state.certificatePem) {
      onError("No certificate available. Please go back and retry issuance.");
      return;
    }

    setLoading(true);
    try {
      const response = await localAgentClient.storeCertificate({
        certificate_pem: state.certificatePem,
        ...(pin !== undefined ? { pin } : {}),
      });

      setState((prev) => ({
        ...prev,
        certificateSubject: response.subject,
        certificateSerial: response.serial_number,
        certificateNotAfter: response.not_after,
      }));
      onComplete();
    } catch (err) {
      if (err instanceof LocalAgentError) {
        if (err.errorCode === "PIN_CANCELLED") {
          onError("PIN entry was cancelled. Please try again and enter your YubiKey PIV PIN when prompted by the native dialog.");
          return;
        }
        if (err.errorCode === "PIN_REQUIRED") {
          setPinErrorMessage(undefined);
          setPinDialogVisible(true);
          return;
        }
        if (err.errorCode === "PIN_FAILED") {
          const retries = err.retriesRemaining;
          const msg =
            retries !== undefined
              ? `PIN verification failed. ${retries} ${retries === 1 ? "retry" : "retries"} remaining.`
              : "PIN verification failed.";
          setPinErrorMessage(msg);
          setPinDialogVisible(true);
          return;
        }
      }
      onError(
        err instanceof Error
          ? err.message
          : "Failed to store certificate on YubiKey.",
      );
    } finally {
      setLoading(false);
    }
  }, [localAgentClient, state.certificatePem, setState, onComplete, onError]);

  const handlePinSubmit = useCallback((pin: string) => {
    setPinDialogVisible(false);
    setPinErrorMessage(undefined);
    void doStore(pin);
  }, [doStore]);

  const handlePinCancel = useCallback(() => {
    setPinDialogVisible(false);
    setPinErrorMessage(undefined);
    onError("PIN is required to proceed");
  }, [onError]);

  // Auto-trigger on mount
  const [triggered, setTriggered] = useState(false);
  useEffect(() => {
    if (!triggered) {
      setTriggered(true);
      void doStore();
    }
  }, [triggered, doStore]);

  if (loading) {
    return (
      <Container>
        <StatusIndicator type="loading">
          Storing certificate on YubiKey…
        </StatusIndicator>
      </Container>
    );
  }

  return (
    <SpaceBetween size="m">
      <Container header={<Header variant="h3">Store Certificate</Header>}>
        <SpaceBetween size="s">
          <p>
            The issued certificate will be written to your YubiKey PIV slot 9a.
          </p>
          <Button
            variant="primary"
            onClick={() => void doStore()}
            loading={loading}
          >
            Store Certificate
          </Button>
        </SpaceBetween>
      </Container>
      <PinDialog
        visible={pinDialogVisible}
        onSubmit={handlePinSubmit}
        onCancel={handlePinCancel}
        errorMessage={pinErrorMessage}
      />
    </SpaceBetween>
  );
}
